<template lang="pug">
.generic-pagination.flex-row(:class="{ loading: page.loading }")
  .flex-1
    .flex-row(v-if="!isEmpty")
      .prev
        a.btn.btn-sm.btn-default(@click="prev()" :disabled="first" :title="label.prev")
          i.fa.fa-chevron-left
      div(style="width: 2px")
      .next
        a.btn.btn-sm.btn-default(@click="next()" :disabled="last" :title="label.next")
          i.fa.fa-chevron-right
      .p10
      .flex-row
        .p5 Page
        .tb-page
          input.input-sm.form-control(
            :class="{ 'out-of-sync': pgNum != page.page, 'invalid-page-number': invalidPageNumber }"
            type="number"
            :readOnly="page.loading"
            @focus="onPageNumFocus"
            @blur="onPageNumBlur"
            v-model="pgNum")
        .p5 of {{ page.getLastPageNumber() }}
      .p10
      .flex-row
        .p5 Size
        .sel-size
          select.input-sm.form-control(
            :readOnly="page.loading"
            v-model="pgSize")
            option(v-for="opt in sizeOptions" :value="opt") {{opt}}
  .p5
    .loading(v-if="page.loading")
      i.fa.fa-spinner.fa-pulse.fa-spin
    .empty(v-else-if="isEmpty") {{ emptyMessage || 'no items' }}
    .one-page(v-else-if="isOnlyOnePage") Total {{page.total}} items
    .pagination-info-text(v-else) {{displayingItemFrom}} to {{ displayingItemTo }} of total {{page.total}} items
</template>

<script>
import debounce from 'debounce'

export default {
  props: {
    page: Object,
    sizeOptions: {
      type: Array,
      default() {
        return [5, 10, 15, 20, 30, 50, 100]
      }
    },
    emptyMessage: String
  },
  created() {
    this.setPage = debounce(this.setPage, 500)
  },
  data() {
    return {
      label: {
        prev: 'Go to previous page',
        next: 'Go to next page'
      },
      pgNum: this.page.getPage(),
      invalidPageNumber: false
    }
  },
  watch: {
    page: {
      handler() {
        this.pgNum = this.page.getPage()
      },
      deep: true
    },
    pgNum() {
      this.invalidPageNumber = false
      this.setPage(this.pgNum)
    }
  },
  methods: {
    onPageNumFocus(event) {
      event.target.select()
    },
    onPageNumBlur() {
      // validate and sync page on blur
      // sync after a timeout since user might be clicking on pagination arrows
      setTimeout(() => {
        // if this.pgNum is not valid
        // restore to the value of this.page.getPage()
        if (this.pgNum !== this.page.getPage()) {
          this.pgNum = this.page.getPage()
        }
      }, 200)
    },
    next() {
      this.page.next()
      this.notifyUpdate()
    },
    prev() {
      this.page.prev()
      this.notifyUpdate()
    },
    setPage(num) {
      const n = Number(num)
      if (n !== this.page.getPage()) { // needs refresh
        if (this.page.isValidPageNumber(n)) {
          this.page.setPage(n)
          this.notifyUpdate()
        } else {
          this.invalidPageNumber = true
        }
      }
    },
    notifyUpdate() {
      this.$emit('update', this.page)
    }
  },
  computed: {
    isEmpty() {
      return this.page.total === 0
    },
    isOnlyOnePage() {
      return this.first && this.last
    },
    displayingItemFrom() {
      return ((this.page.page - 1) * (this.page.size)) + 1
    },
    displayingItemTo() {
      return Math.min((this.displayingItemFrom - 1) + this.page.size, this.page.total)
    },
    first() {
      return this.page.isFirstPage()
    },
    last() {
      return this.page.isLastPage()
    },
    pgSize: {
      get() {
        return this.page.getSize()
      },
      set(n) {
        this.page.setSize(n)
        if (this.page.getPage() > this.page.getLastPageNumber()) {
          this.page.setPage(this.page.getLastPageNumber())
        }
        this.notifyUpdate()
      }
    },

    pagingControlsVisible() {
      return this.page.total > 0
    }
  }
}
</script>

<style lang="scss">
  .generic-pagination {
    padding: 5px;
    border: 1px solid #dddddd;
    background: #efefef;
    font-size: 14px;
    a {
      cursor: pointer;
    }
    a[disabled] {
      opacity: 0.5;
    }
    .form-control {
      text-align: right;
      &.out-of-sync {
        opacity: 0.7;
      }
      font-size: 14px;
    }
    &.loading {
      opacity: 0.7;
    }
    .tb-page input {
      width: 65px;
    }

    .invalid-page-number {
      color: red
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/common/GenericPagination.vue